import React, {
	memo,
	useCallback,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Form } from 'semantic-ui-react';

import {
	handleError,
	log
} from '../../../services/utils';

import useForm from '../../../hooks/useForm';
import useToggle from '../../../hooks/useToggle';

import {
	authenticate,
	getUserProfile
} from '../../../api/nora/auth';

import { useAuthAction } from '../../../contexts/AuthContext';
import { useUIAction } from '../../../contexts/UIContext';

import Alert from '../../Alert';
import AuthInput from '../AuthInput';
import SubmitButton from '../SubmitButton';
import TextButton from '../../Shared/TextButton';

import styles from '../auth.module.css';

const LoginForm = ( { toggleForgotPass } ) => {
	const history = useHistory();

	const { signIn } = useAuthAction();
	const { setScreenLoader } = useUIAction();

	const {
		formData,
		updateField
	} = useForm( {
		userName: '',
		password: ''
	} );

	const [ errorModal, toggleErrorModal ] = useToggle( false );
	const [ errorModalMessage, setErrorModalMessage ] = useState( '' );

	const handleSubmit = useCallback( async () => {
		setScreenLoader( 'Authorizing Access...' );

		try {
			const {
				token,
				valid,
				userID,
				portal,
				otp
			} = await authenticate( formData );

			if ( valid ) {
				if ( token ) {
					sessionStorage.setItem( 'token', token );
				} else {
					throw new Error( 'token not found' );
				}

				if ( userID ) {
					const userProfile = await getUserProfile( userID );

					if ( userProfile ) {
						signIn( { ...userProfile, portal } );
					} else {
						throw new Error( 'userProfile not found' );
					}
				} else {
					throw new Error( 'userID not found' );
				}
			} else if ( userID ) {
				history.push( {
					pathname: 'authenticate',
					state: {
						credentials: formData,
						otp,
						userID
					}
				} );
			} else {
				throw new Error( 'userID not found' );
			}

			setScreenLoader( '' );
		} catch ( error ) {
			setScreenLoader( '' );
			const errorMessage = handleError( error );

			setErrorModalMessage( errorMessage );
			toggleErrorModal();
		}
	}, [ formData, history, setScreenLoader, signIn, toggleErrorModal ] );

	const handleKeyDown = useCallback( e => {
		if ( e.key === 'Enter' ) {
			handleSubmit();
		}
	}, [ handleSubmit ] );

	return (
		<>
			{ log( 'loginForm' ) }
			<Form className={ styles.inputs }>
				<AuthInput
					icon="user"
					name="userName"
					onChange={ updateField }
					placeholder="Username"
					value={ formData.userName }
					onKeyDown={ handleKeyDown }
				/>
				<AuthInput
					autoComplete="chrome-off"
					icon="lock"
					name="password"
					onChange={ updateField }
					placeholder="Password"
					type="password"
					value={ formData.password }
					onKeyDown={ handleKeyDown }
				/>
			</Form>
			<SubmitButton
				buttonText="Authorize Access"
				onSubmit={ handleSubmit }
			/>
			<TextButton
				buttonText="Don't Have an Account? Request Authorization"
				className={ styles.textButton }
			/>
			<TextButton
				buttonText="Forgot Password"
				className={ styles.textButton }
				onClick={ toggleForgotPass }
			/>
			<Alert
				name="login-error-alert"
				heading="Unable to Authorize Access"
				message={ errorModalMessage }
				open={ errorModal }
				closeModal={ toggleErrorModal }
				type="error"
			/>
			<small
				style={ {
					position: 'absolute',
					top: '0.25rem',
					right: '0.5rem'
				} }
			>
				version { JSON.parse( localStorage.getItem( 'appVersion' ) ) }
			</small>
		</>
	);
};

LoginForm.propTypes = { toggleForgotPass: PropTypes.func };

export default memo( LoginForm );
